/**
 * Module Permissions Store — Product Portal
 *
 * Manages role-based module visibility and accessibility.
 * Stores resolved permissions after login.
 */
import { defineStore } from 'pinia';
import {
  resolvePermissionsForUser,
  isModuleAccessible,
  type ModulePermissions,
} from '@/core/services/permissions.service';

/** In-flight fetch mutex — prevents duplicate concurrent requests. */
let _fetchPromise: Promise<void> | null = null;

interface PermissionsState {
  /** Resolved permissions for the current user (union of all roles) */
  permissions: ModulePermissions | null;
  /** Whether permissions have been loaded */
  isLoaded: boolean;
  /** Whether a fetch is currently in progress */
  isLoading: boolean;
  /** Error message from the last failed fetch, if any */
  error: string | null;
}

export const usePermissionsStore = defineStore('permissions', {
  state: (): PermissionsState => ({
    permissions: null,
    isLoaded: false,
    isLoading: false,
    error: null,
  }),

  getters: {
    /**
     * Check if a specific module is accessible for the current user
     */
    hasModuleAccess: (state) => {
      return (moduleKey: keyof ModulePermissions): boolean => {
        if (!state.permissions) return false;
        return isModuleAccessible(state.permissions, moduleKey);
      };
    },

    /**
     * Get all accessible module keys for the current user
     */
    accessibleModules: (state) => {
      if (!state.permissions) return [];

      const modules: (keyof ModulePermissions)[] = [
        'productManagement',
        'tenantManagement',
        'userManagement',
        'enterprisesManagement',
        'settings',
        'recall',
        'auditLogs',
      ];

      return modules.filter(mod => isModuleAccessible(state.permissions, mod));
    },
  },

  actions: {
    /**
     * Fetch and resolve module permissions for the current user.
     *
     * - Calls the backend to get the role-module-matrix
     * - Resolves permissions based on user's roles using union logic
     * - Updates the store state
     * - Sets error state if the fetch fails
     *
     * This should be called once after login.
     */
    async fetchPermissions(): Promise<void> {
      // Skip duplicate loaded requests.
      if (this.isLoaded) return;

      // Dedupe concurrent callers so guards can await the same in-flight request.
      if (_fetchPromise) {
        return _fetchPromise;
      }

      this.isLoading = true;
      this.error = null;

      _fetchPromise = (async () => {
        try {
          const resolved = await resolvePermissionsForUser();
          this.permissions = resolved;
          this.isLoaded = true;
        } catch (err) {
          this.error = err instanceof Error ? err.message : 'Failed to fetch permissions';
          this.isLoaded = false;
          this.permissions = null;
          throw err; // Re-throw so caller can handle
        } finally {
          this.isLoading = false;
          _fetchPromise = null;
        }
      })();

      return _fetchPromise;
    },

    /**
     * Check if a module is accessible (convenience method)
     */
    isModuleAllowed(moduleKey: keyof ModulePermissions): boolean {
      return this.hasModuleAccess(moduleKey);
    },

    /**
     * Reset permissions when user logs out
     */
    reset(): void {
      _fetchPromise = null;
      this.permissions = null;
      this.isLoaded = false;
      this.isLoading = false;
      this.error = null;
    },
  },
});
